// frontend/src/pages/CodeDetailPage.jsx
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
// react-leaflet map — CSS must be imported before the components so Leaflet's
// tile styles are available. The import is safe to bundle with Vite / CRA.
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import { getCode, deleteCode, downloadQr } from "../api/codes";
import { getCodeAnalyticsSummary, exportScans, getHeatmap } from "../api/analytics";
import EditCodeModal from "../components/EditCodeModal";
import ConfirmDialog from "../components/ConfirmDialog";
import Spinner from "../components/Spinner";
import ErrorBanner from "../components/ErrorBanner";
import ABAnalyticsTab from "../components/ABAnalyticsTab";
import CodeDetailStats from "./CodeDetailStats";

/**
 * ActiveBadge — pill indicating whether a code is active or inactive.
 *
 * @param {{ isActive: boolean }} props
 */
function ActiveBadge({ isActive }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}
ActiveBadge.propTypes = { isActive: PropTypes.bool.isRequired };

// --------------------------------------------------------------------------- //
// Helpers                                                                      //
// --------------------------------------------------------------------------- //

/**
 * Derive a circle radius (px) from a scan count for heatmap bubbles.
 * Minimum 5 px, maximum 28 px, grows with √count.
 *
 * @param {number} count
 * @returns {number}
 */
function heatRadius(count) {
  return Math.max(5, Math.min(28, 5 + Math.sqrt(count) * 2.5));
}

// --------------------------------------------------------------------------- //
// ScanHeatmapSection                                                           //
// --------------------------------------------------------------------------- //

/**
 * ScanHeatmapSection — renders a Leaflet map with CircleMarker bubbles for
 * each 0.5°-grid scan cluster, or a plan-gate upgrade prompt when the feature
 * is not available on the current plan.
 *
 * @param {{ points: Array<{lat: number, lng: number, count: number}>|null, available: boolean }} props
 */
function ScanHeatmapSection({ points, available }) {
  if (!available) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center space-y-2">
        <p className="text-sm font-semibold text-gray-700">
          📍 Location Heatmap — Pro feature
        </p>
        <p className="text-xs text-gray-500">
          Upgrade to Pro or Business to see where in the world your QR code is
          being scanned.
        </p>
        <a
          href="/billing"
          className="inline-block mt-1 px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          Upgrade plan
        </a>
      </div>
    );
  }

  if (points === null) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 flex justify-center">
        <Spinner size={24} className="text-blue-500" />
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-500">
        No location data yet — scans will appear here once geo lookup resolves.
      </div>
    );
  }

  // Compute map centre as the weighted mean of all points.
  const totalCount = points.reduce((s, p) => s + p.count, 0);
  const centerLat = points.reduce((s, p) => s + p.lat * p.count, 0) / totalCount;
  const centerLng = points.reduce((s, p) => s + p.lng * p.count, 0) / totalCount;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-700">📍 Scan Locations</span>
        <span className="text-xs text-gray-400">({points.length} cluster{points.length !== 1 ? "s" : ""})</span>
      </div>
      {/* MapContainer needs an explicit height to render. */}
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={3}
        style={{ height: "360px", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {points.map((p) => (
          <CircleMarker
            key={`${p.lat},${p.lng}`}
            center={[p.lat, p.lng]}
            radius={heatRadius(p.count)}
            pathOptions={{
              fillColor: "#3b82f6",
              fillOpacity: 0.55,
              color: "#1d4ed8",
              weight: 1,
            }}
          >
            <Tooltip>
              {p.count} scan{p.count !== 1 ? "s" : ""}
              <br />
              {p.lat.toFixed(1)}°, {p.lng.toFixed(1)}°
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

ScanHeatmapSection.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
    })
  ),
  available: PropTypes.bool.isRequired,
};

ScanHeatmapSection.defaultProps = {
  points: null,
};

// --------------------------------------------------------------------------- //
// CodeDetailPage                                                               //
// --------------------------------------------------------------------------- //

/**
 * CodeDetailPage — header, action buttons, stat cards, and 30-day line chart
 * for a single QR code.
 */
function CodeDetailPage() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [codeObj, setCodeObj] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  // Heatmap state is managed independently so the main page load is never
  // blocked by the extra geo request.
  const [heatmapPoints, setHeatmapPoints] = useState(null); // null = loading
  const [heatmapAvailable, setHeatmapAvailable] = useState(true);

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // "overview" = stats + heatmap; "abtest" = A/B variant analytics (P14-G8).
  const [tab, setTab] = useState("overview");

  // ── Load ────────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getCode(code), getCodeAnalyticsSummary(code)])
      .then(([c, a]) => {
        if (!cancelled) {
          setCodeObj(c);
          setAnalytics(a);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load code details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code, retryKey]);

  // Fetch heatmap data independently so the page renders before geo resolves.
  useEffect(() => {
    let cancelled = false;
    setHeatmapPoints(null);
    setHeatmapAvailable(true);
    getHeatmap(code)
      .then((data) => {
        if (!cancelled) setHeatmapPoints(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 402) {
          setHeatmapAvailable(false);
          setHeatmapPoints([]);
        } else {
          // Non-402 error (network, 404): silently show empty state.
          setHeatmapPoints([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleUpdated = (updated) => {
    setCodeObj(updated);
    setShowEdit(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteCode(code);
      toast.success(`"${code}" deleted.`);
      navigate("/codes", { replace: true });
    } catch {
      toast.error("Failed to delete code.");
      setDeleting(false);
    }
  };

  const handleExportScans = async () => {
    setExporting(true);
    try {
      await exportScans(code);
    } catch {
      toast.error("Failed to export scans.");
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadQr = async () => {
    setDownloading(true);
    try {
      await downloadQr(code);
    } catch {
      toast.error("Failed to download QR image.");
    } finally {
      setDownloading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-16 flex justify-center">
        <Spinner size={32} className="text-blue-500" />
      </div>
    );
  }

  if (error || !codeObj) {
    return (
      <div className="max-w-4xl mx-auto py-8 space-y-4">
        <Link to="/codes" className="text-sm text-gray-500 hover:text-gray-800">
          ← QR Codes
        </Link>
        <ErrorBanner
          message={error ?? "Code not found."}
          onRetry={error ? () => setRetryKey((k) => k + 1) : undefined}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Back link */}
      <Link to="/codes" className="text-sm text-gray-500 hover:text-gray-800">
        ← QR Codes
      </Link>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 font-mono">
                {codeObj.code}
              </h1>
              <ActiveBadge isActive={codeObj.is_active} />
            </div>
            <p className="text-sm text-gray-500 break-all">{codeObj.text}</p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleDownloadQr}
              disabled={downloading}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              {downloading ? "Downloading…" : "↓ Download QR"}
            </button>
            <button
              onClick={handleExportScans}
              disabled={exporting}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              {exporting ? "Exporting…" : "↓ Export scans CSV"}
            </button>
            <button
              onClick={() => setShowEdit(true)}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDelete(true)}
              className="px-3 py-1.5 text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button
          onClick={() => setTab("overview")}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
            tab === "overview"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setTab("abtest")}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
            tab === "abtest"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          A/B Test
        </button>
      </div>

      {tab === "overview" ? (
        <>
          {/* Stats + chart */}
          {analytics && <CodeDetailStats analytics={analytics} code={code} />}

          {/* Location heatmap — Pro+ feature */}
          <ScanHeatmapSection
            points={heatmapPoints}
            available={heatmapAvailable}
          />
        </>
      ) : (
        <ABAnalyticsTab codeId={codeObj.id} />
      )}

      {/* Modals */}
      {showEdit && (
        <EditCodeModal
          codeObj={codeObj}
          onClose={() => setShowEdit(false)}
          onUpdated={handleUpdated}
        />
      )}
      {showDelete && (
        <ConfirmDialog
          title={`Delete "${code}"?`}
          message="This will permanently delete the code and all scan history. This cannot be undone."
          confirmLabel="Delete"
          dangerous
          loading={deleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
}

export default CodeDetailPage;
